import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown, ChevronLeft, ChevronRight as ChevronOpen,
  Layers, ShieldCheck, Users, Building2, FileText,
  Lock, Briefcase, ClipboardList, Warehouse, BarChart,
  Settings2, FolderSearch,
} from "lucide-react";
import { FiLayers } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import axios from "../services/api";
import { slugify } from "../utils/slugify";


// 🔁 Slug helpers
const deslugify = (slug) =>
  slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

const adminSections = {
  Admin: ["Users", "Companies", "Reports", "Authorized Reports", "Departments"],
};

const adminIcons = {
  Users,
  Companies: Building2,
  Reports: FileText,
  "Authorized Reports": Lock,
  Departments: FiLayers,
};

const departmentIcons = {
  Accounting: BarChart,
  Procurement: ClipboardList,
  Inventory: Warehouse,
  "Project Managment": Briefcase,
  Tools: Settings2,
  default: FolderSearch,
};

const sidebarVariants = {
  open: { width: 288 },
  collapsed: { width: 72 },
};

const itemVariants = {
  hidden: { opacity: 0, height: 0, y: -10 },
  visible: { opacity: 1, height: "auto", y: 0 },
  exit: { opacity: 0, height: 0, y: -10 },
};

export default function Sidebar({ collapsed, onToggleCollapse }) {
  const [openSections, setOpenSections] = useState({});
  const [groupedReports, setGroupedReports] = useState({});
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    const fetchAuthorizedReports = async () => {
      try {
        const res = await axios.get("/authorized-reports/user");
        if (res.data && typeof res.data === "object") {
          setGroupedReports(res.data);
        } else {
          console.warn("⚠️ Invalid grouped report format", res.data);
        }
      } catch (error) {
        console.error("❌ Failed to fetch authorized reports", error);
      }
    };

    if (user) {
      setGroupedReports({});
      fetchAuthorizedReports();
    }
  }, [user]);

  // Auto-expand active report section
  useEffect(() => {
    Object.entries(groupedReports).forEach(([dept, reports]) => {
      reports.forEach((r) => {
        const reportPath = `/reports/${slugify(dept)}/${slugify(r.title)}`;
        if (location.pathname === reportPath) {
          setOpenSections((prev) => ({ ...prev, [dept]: true }));
        }
      });
    });
  }, [location.pathname, groupedReports]);

  const toggleSection = (section) => {
    setOpenSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <motion.aside
      initial={collapsed ? "collapsed" : "open"}
      animate={collapsed ? "collapsed" : "open"}
      variants={sidebarVariants}
      transition={{ type: "spring", stiffness: 260, damping: 25 }}
      className="bg-gradient-to-br from-gray-900 to-gray-800 text-white shadow-xl fixed h-full z-50 flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center justify-between py-6 px-4">
        <Layers className="text-blue-400" size={28} />
        {!collapsed && <h2 className="text-2xl font-bold ml-2">Reports</h2>}
        <motion.button
          whileTap={{ scale: 0.9 }}
          onClick={onToggleCollapse}
          className="bg-blue-500 hover:bg-blue-600 rounded-full p-1 shadow-lg transition duration-200"
        >
          {collapsed ? <ChevronOpen size={20} /> : <ChevronLeft size={20} />}
        </motion.button>
      </div>

      {/* Main Navigation */}
      <nav className="flex-grow px-2 space-y-2 overflow-y-auto">
        {/* 🔷 Department Reports */}
        {Object.entries(groupedReports).map(([dept, reports]) => {
          const DepartmentIcon = departmentIcons[dept] || departmentIcons.default;
          const slugDept = slugify(dept);

          return (
            <div key={dept}>
              <motion.button
                whileTap={{ scale: 0.98 }}
                whileHover={{ scale: 1.01 }}
                onClick={() => toggleSection(dept)}
                className={`w-full flex items-center justify-between py-2 px-2 rounded-lg ${
                  openSections[dept] ? "bg-gray-700" : "hover:bg-gray-700"
                } transition-all duration-200`}
              >
                <span className="flex items-center">
                  <DepartmentIcon
                    size={20}
                    className="text-blue-400 hover:animate-pulse"
                  />
                  {!collapsed && (
                    <span className="ml-3 font-semibold tracking-wide">{dept}</span>
                  )}
                </span>
                {!collapsed && (
                  <motion.span
                    animate={{ rotate: openSections[dept] ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronDown size={20} />
                  </motion.span>
                )}
              </motion.button>

              <AnimatePresence>
                {openSections[dept] && !collapsed && (
                  <motion.ul
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    variants={itemVariants}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="mt-2 ml-10 border-l-2 border-blue-500 pl-4"
                  >
                    {reports.length === 0 ? (
                      <li className="text-sm italic text-gray-400 py-2">
                        No Reports Available
                      </li>
                    ) : (
                      reports.map((r) => {
                        const reportSlug = slugify(r.title);
                        const reportPath = `/reports/${slugDept}/${reportSlug}`;
                        const isActive = location.pathname === reportPath;

                        return (
                          <motion.li key={r.id} whileHover={{ x: 6 }} className="mb-2">
                            <Link
                              to={reportPath}
                              className={`block py-2 px-3 rounded-md font-medium transition duration-200 ${
                                isActive
                                  ? "bg-blue-500 text-white shadow-md"
                                  : "hover:bg-gray-600 hover:text-yellow-300"
                              }`}
                            >
                              {r.title}
                            </Link>
                          </motion.li>
                        );
                      })
                    )}
                  </motion.ul>
                )}
              </AnimatePresence>
            </div>
          );
        })}

        {/* 🛡 Admin Section */}
        {user?.role === "admin" && (
          <>
            <hr className="my-4 border-gray-600" />
            <div>
              <motion.button
                whileTap={{ scale: 0.98 }}
                whileHover={{ scale: 1.01 }}
                onClick={() => toggleSection("Admin")}
                className={`w-full flex items-center justify-between py-2 px-2 rounded-lg ${
                  openSections["Admin"] ? "bg-gray-700" : "hover:bg-gray-700"
                } transition-all duration-200`}
              >
                <span className="flex items-center">
                  <ShieldCheck className="text-yellow-400 hover:animate-pulse" size={20} />
                  {!collapsed && (
                    <span className="ml-3 font-semibold tracking-wide">Admin Control</span>
                  )}
                </span>
                {!collapsed && (
                  <motion.span
                    animate={{ rotate: openSections["Admin"] ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronDown size={20} />
                  </motion.span>
                )}
              </motion.button>

              <AnimatePresence>
                {openSections["Admin"] && !collapsed && (
                  <motion.ul
                    key="Admin"
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                    variants={itemVariants}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                    className="mt-2 ml-10 border-l-2 border-yellow-400 pl-4"
                  >
                    {adminSections["Admin"].map((item) => {
                      const AdminIcon = adminIcons[item];
                      const path = `/admin/${slugify(item)}`;
                      return (
                        <motion.li key={item} whileHover={{ x: 6 }} className="mb-2">
                          <Link
                            to={path}
                            className={`flex items-center py-2 px-3 rounded-md font-medium transition duration-200 ${
                              location.pathname === path
                                ? "bg-yellow-500 text-white shadow-md"
                                : "hover:bg-gray-600 hover:text-yellow-300"
                            }`}
                          >
                            <AdminIcon
                              size={18}
                              className="mr-2 hover:animate-pulse transition duration-300"
                            />
                            {item}
                          </Link>
                        </motion.li>
                      );
                    })}
                  </motion.ul>
                )}
              </AnimatePresence>
            </div>
          </>
        )}
      </nav>
    </motion.aside>
  );
}
